import React, { useEffect, useState } from 'react'
import { OrderItemsInPackages, OrderItemsTable } from '../../components'
import { Button } from '../../components/shadcn/Button'
import { ClearanceItems } from './ClearanceItems'
import { OrderReturns } from './OrderReturns'
import { useOrderPicking } from './lib/hooks/useOrderPicking'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { graphql, useFragment } from '../../gql'
import { useGQLFetcher } from '../../context/GQLClientProvider'
import { Input } from '../../components/shadcn/Input'
import { faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { useGQLMutation } from '../../hooks/GQLUtilityHooks'
import { toastError, toastPromise, toastSuccess } from '../../lib/toastHelpers'
import { buildCameraConstraints } from '../../lib/Camera'
import { useOrderDetailContext } from './OrderDetailContext'
import { notifyPickingActivity } from './lib/hooks/pickingActivityEvents'
import { useSettingsStore } from '../../contexts/SettingsContext'

const updateClearanceItemMutation = graphql(`
  mutation UpdateClearanceItemBulk($id: String!, $order: Int!, $picked: Int, $packed: Int) {
    updateClearanceItem(id: $id, order: $order, picked: $picked, packed: $packed) {
      data {
        id
        picked
        packed
      }
      errors
    }
  }
`)

const updateOrder_OrderProductsMutation = graphql(`
  mutation UpdateOrder_OrderProducts($id: Int!, $boxes: [String!]!) {
    updateOrder(id: $id, boxes: $boxes) {
      data {
        id
        boxes
      }
    }
  }
`)

const CreateFileFromCameraMutation = graphql(`
  mutation CreateOrderFileFromCameraInProducts(
    $base64Data: String!
    $text: String!
    $originalFilename: String!
    $mimeType: String!
    $order: Int
  ) {
    createFile(
      base64Data: $base64Data
      text: $text
      originalFilename: $originalFilename
      mimeType: $mimeType
      order: $order
    ) {
      data {
        id
      }
      errors
    }
  }
`)

export const OrderProducts_OrderFragment = graphql(`
  fragment OrderProducts_OrderFragment on Order {
    id
    boxes
    items {
      id
      type
      ...OrderItemsTable_OrderItemFragment
    }
    clearanceItems {
      id
      amount
    }
    processStatus {
      id
      name
    }
  }
`)

export const OrderProducts: React.FC = () => {
  const context = useOrderDetailContext()
  const order = useFragment(OrderProducts_OrderFragment, context.order)
  const settingsStore = useSettingsStore()

  const [openTab, setOpenTab] = useState<number>(1)
  const [canBeReturned, setCanBeReturned] = useState<boolean>(false)
  const [box, setBox] = useState<string>('')
  const updateClearanceItem = useGQLFetcher(updateClearanceItemMutation)

  const { mutateAsync, isPending } = useGQLMutation(updateOrder_OrderProductsMutation, ['updateOrder_OrderProducts'], {
    onSuccess: data => {
      queryClient.setQueryData(['order', { id: order?.id }], (old: any) => {
        return {
          order: {
            ...old.order,
            data: {
              ...old.order.data,
              boxes: data.boxes,
            },
          },
        }
      })
    },
  })
  const queryClient = useQueryClient()

  const { mutateAsync: createFileFromCamera } = useGQLMutation(CreateFileFromCameraMutation, ['createFile'])

  const { mutate: markAllClearanceItems, isPending: isMarkingAllClearanceItems } = useMutation({
    mutationFn: async () =>
      Promise.all(
        order.clearanceItems.map(clearanceItem =>
          updateClearanceItem({
            id: clearanceItem.id,
            order: order.id,
            ...(order.processStatus?.id === 'picking' ? { picked: clearanceItem.amount } : {}),
            ...(order.processStatus?.id === 'packing' ? { packed: clearanceItem.amount } : {}),
          }),
        ),
      ),
    mutationKey: ['updateClearanceItems'],
    onSuccess: data => {
      const clearanceItems = data.map(item => item.updateClearanceItem.data)
      queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
        return {
          ...old,
          order: {
            ...old.order,
            data: {
              ...old.order.data,
              clearanceItems: old.order.data.clearanceItems.map(oldItem =>
                clearanceItems.find(item => item.id === oldItem.id)
                  ? { ...oldItem, ...clearanceItems.find(item => item.id === oldItem.id) }
                  : oldItem,
              ),
            },
          },
        }
      })
      const phase =
        order.processStatus?.id === 'packing' ? 'packing' : order.processStatus?.id === 'picking' ? 'picking' : null
      if (phase) {
        notifyPickingActivity({ orderId: Number(order.id), phase, source: 'bulk' })
      }
    },
  })

  useEffect(() => {
    if (order) {
      const canReturnOrder =
        order.processStatus?.id === 'packed' ||
        order.processStatus?.id === 'dispatched' ||
        order.processStatus?.id === 'delivered' ||
        order.processStatus?.id === 'returned'
      setCanBeReturned(canReturnOrder)
      if (canReturnOrder) {
        if (order.processStatus?.id === 'returned') {
          setOpenTab(3)
        } else {
          setOpenTab(2)
        }
      }
    }

    if (order.processStatus?.id === 'packing' || order.processStatus?.id === 'packed') {
      setOpenTab(2)
    }
  }, [])

  useOrderPicking()

  const addBox = async (boxArg: string = null) => {
    const boxToAdd = (typeof boxArg === 'string' ? boxArg : box) || ''
    if (boxToAdd?.length > 0) {
      toastPromise(mutateAsync({ id: order.id, boxes: Array.from(new Set([...order.boxes, boxToAdd])) }), {
        success: 'Přepravka přidána',
        pending: 'Přidávám přepravku',
        error: 'Nepodařilo se přidat přepravku',
      })
      setBox('')
    }
  }

  const removeBox = async (box: string) => {
    const boxes = [...order.boxes]
    const indexToRemove = boxes.findIndex(b => b === box)
    if (indexToRemove !== -1) {
      boxes.splice(indexToRemove, 1)
    }
    toastPromise(mutateAsync({ id: order.id, boxes }), {
      success: 'Přepravka odebrána',
      pending: 'Odebírám přepravku',
      error: 'Nepodařilo se odebrat přepravku',
    })
  }

  const allClearanceItemsMarked = order.clearanceItems.every(
    item => item[order.processStatus?.id === 'picking' ? 'picked' : 'packed'] === item.amount,
  )

  const uploadImageDataUrl = async (dataUrl: string | null) => {
    if (!dataUrl) return
    if (!order?.id) {
      toastError('Chybí ID objednávky, nelze nahrát fotku.')
      return
    }

    try {
      const [meta, base64Part] = dataUrl.split(',')
      const mimeMatch = meta.match(/data:(.*);base64/)
      const mimeType = mimeMatch?.[1] || 'image/jpeg'

      await createFileFromCamera({
        base64Data: base64Part,
        text: 'Foto z kamery',
        originalFilename: `${order.id}-${Date.now()}-camera.jpg`,
        mimeType,
        order: order.id,
      })

      toastSuccess('Fotka byla nahrána k objednávce')
      queryClient.invalidateQueries({ queryKey: ['order', { id: order.id }] })
    } catch (error: any) {
      console.error('Error uploading camera photo', error)
      toastError('Nepodařilo se nahrát fotku z kamery')
    }
  }

  const handleQuickCaptureAndUpload = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toastError('Kamera není v tomto prohlížeči dostupná.')
      return
    }

    try {
      const constraints = buildCameraConstraints(settingsStore.cameraDeviceId)

      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      const video = document.createElement('video')
      video.srcObject = stream
      await video.play()

      // wait for camera to focus
      await new Promise(resolve => setTimeout(resolve, 700))

      const width = video.videoWidth || 1280
      const height = video.videoHeight || 720

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        stream.getTracks().forEach(track => track.stop())
        return
      }

      ctx.drawImage(video, 0, 0, width, height)
      const dataUrl = canvas.toDataURL('image/jpeg')

      stream.getTracks().forEach(track => track.stop())

      await uploadImageDataUrl(dataUrl)
    } catch (error) {
      console.error('Error capturing quick photo', error)
      toastError('Nepodařilo se pořídit fotku z kamery')
    }
  }

  return (
    <div>
      <ul className="p-sub-main flex justify-between border-b border-cool-grey" role="tablist">
        <div className={'flex gap-4'}>
          <div className={'my-auto'}>
            <div className={'flex bg-gray-100 rounded-xl'}>
              {!canBeReturned && (
                <Button
                  variant={openTab === 1 ? 'default' : 'ghost'}
                  size="lg"
                  className={`rounded-r-none px-6 border-r-0 ${
                    openTab === 1 ? 'bg-primary text-primary-foreground text-white' : 'bg-background text-foreground'
                  }`}
                  onClick={e => {
                    e.preventDefault()
                    setOpenTab(1)
                  }}>
                  Položky k expedici
                </Button>
              )}
              <Button
                variant={openTab === 2 ? 'default' : 'ghost'}
                size="lg"
                className={`${!canBeReturned ? 'rounded-l-none' : ''} ${canBeReturned ? 'rounded-r-none px-6 border-r-0' : ''} ${
                  openTab === 2 ? 'bg-primary text-primary-foreground text-white' : 'bg-background text-foreground'
                }`}
                onClick={e => {
                  e.preventDefault()
                  setOpenTab(2)
                }}>
                Položky objednávky
              </Button>
              {canBeReturned && (
                <Button
                  variant={openTab === 3 ? 'default' : 'ghost'}
                  size="lg"
                  className={`rounded-l-none px-6 ${
                    openTab === 3 ? 'bg-primary text-primary-foreground text-white' : 'bg-background text-foreground'
                  }`}
                  onClick={e => {
                    e.preventDefault()
                    setOpenTab(3)
                  }}>
                  Vratky
                </Button>
              )}
            </div>
          </div>
          {(order.processStatus?.id === 'picking' || order.processStatus?.id === 'packing') && (
            <div className={'flex gap-2 items-center'}>
              <div className={'flex rounded-lg overflow-hidden'}>
                <Input
                  placeholder={'Přepravka'}
                  type={'text'}
                  value={box}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      addBox()
                    }
                  }}
                  onChange={val => setBox(val.target.value)}
                  className={'rounded-r-none border-r-0 focus:z-10'}
                />
                <Button
                  variant="secondary"
                  size="lg"
                  icon={faPlus}
                  title={'Přidat přepravku'}
                  disabled={isPending}
                  onClick={() => addBox()}
                  className={'rounded-l-none'}>
                  {'Přidat'}
                </Button>
              </div>
              {order.boxes.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {order.boxes.map((boxItem, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center px-2 h-12 rounded-xl text-sm font-medium bg-secondary text-blue-800 border border-blue-200 gap-2">
                      <div className="flex items-center font-bold text-lg pl-2">
                        {/* <FontAwesomeIcon icon={faCartFlatbedEmpty} className="mx-2" /> */}
                        {boxItem}
                      </div>
                      <Button variant="ghost" size="icon" icon={faXmark} onClick={() => removeBox(boxItem)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {order.processStatus?.id === 'packing' && (
            <Button variant="outline" size="lg" icon={['far', 'camera']} onClick={handleQuickCaptureAndUpload}>
              Vyfotit
            </Button>
          )}
          {(order.processStatus?.id === 'picking' || order.processStatus?.id === 'packing') &&
            openTab === 1 &&
            !allClearanceItemsMarked && (
              <Button
                variant="outline"
                icon={['far', 'check-square']}
                title={'Označit vše'}
                disabled={isMarkingAllClearanceItems}
                onClick={() => markAllClearanceItems()}>
                {'Označit vše'}
              </Button>
            )}
        </div>
      </ul>
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full rounded scrollable-content">
        <div>
          <div className={openTab === 1 ? 'block' : 'hidden'} id="link1">
            <ClearanceItems />
          </div>
          <div className={openTab === 2 ? 'block' : 'hidden'} id="link2">
            {order.processStatus?.id === 'received' ||
            order.processStatus?.id === 'recieved' ||
            order.processStatus?.id === 'picking' ? (
              <OrderItemsTable items={order.items?.filter(oi => oi.type === 'product') || []} />
            ) : (
              <OrderItemsInPackages orderId={order.id} />
            )}
          </div>
          <div className={openTab === 3 ? 'block' : 'hidden'} id="link3">
            {openTab === 3 && <OrderReturns />}
          </div>
        </div>
      </div>
    </div>
  )
}
